import makeStyles from '@mui/styles/makeStyles';
import React from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {color, radius, type} from "common/tokens";
import CtaButton from "components/redesign/CtaButton";

// A change to one occurrence of a series has to say how far it reaches. A
// single event only ever applies to itself, so it gets a plain confirm.
export const seriesOptions = t => [
  ['THIS', t('thisEventCaps')],
  ['FOLLOWING', t('thisAndFollowingCaps')],
  ['ALL', t('allEventsCaps')],
];

export const singleOption = label => [['THIS', label]];

const useStyles = makeStyles({
  scrim: {
    position: 'fixed',
    inset: 0,
    background: color.SCRIM,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1400,
    padding: 16,
    animation: '$fadeIn 220ms ease-out',
  },
  dialog: {
    background: color.SURFACE,
    borderRadius: radius.MEDIA_CARD,
    padding: '32px',
    width: 420,
    maxWidth: '100%',
    boxSizing: 'border-box',
    animation: '$growIn 240ms ease-out',
  },
  '@keyframes growIn': {
    '0%': {opacity: 0, transform: 'scale(0.86)'},
    '100%': {opacity: 1, transform: 'scale(1)'},
  },
  '@keyframes fadeIn': {
    '0%': {opacity: 0},
    '100%': {opacity: 1},
  },
  title: {
    ...type.cardHeading,
    color: color.TEXT,
    margin: '0 0 8px',
  },
  prompt: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '0 0 20px',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

// Portalled to the body: the pages this opens from sit inside an animated
// container, whose transform pins any z-index below the event form's modal.
//
// A series asks which occurrences it reaches, so its options start plain and
// `chosenVariant` fills the one that was picked. A single confirm has nothing
// to choose between, so its one action carries that weight from the start —
// red for a delete — against a plain Cancel.
const EventSeriesDialog = ({
  open,
  title,
  prompt,
  options,
  chosen,
  chosenVariant = 'primary',
  busy,
  onChoose,
  onDismiss,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  if (!open) {
    return null;
  }

  const single = options.length === 1;

  return createPortal((
    <div className={classes.scrim} role={'presentation'} onClick={onDismiss}>
      <div className={classes.dialog} role={'dialog'} onClick={e => e.stopPropagation()}>
        <p className={classes.title}>{title}</p>
        <p className={classes.prompt}>{prompt}</p>
        <div className={classes.actions}>
          {options.map(([applyTo, label]) => (
            <CtaButton
              key={applyTo}
              fullWidth
              variant={single || chosen === applyTo ? chosenVariant : 'secondary'}
              loading={busy && chosen === applyTo}
              disabled={busy}
              label={label}
              onClick={() => onChoose(applyTo)}
            />
          ))}
          {/* A series lists its three reaches and the scrim is the way out; a
              single confirm needs the way out spelled next to the action. */}
          {single && (
            <CtaButton
              fullWidth
              variant={'secondary'}
              disabled={busy}
              label={t('cancelLabel')}
              onClick={onDismiss}
            />
          )}
        </div>
      </div>
    </div>
  ), document.body);
};

export default EventSeriesDialog;
